import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnInit,
    OnDestroy
} from '@angular/core';
import {
    FormGroup,
    FormControl,
    Validators
} from '@angular/forms';
import { BehaviorSubject, Subject, Observable, combineLatest } from 'rxjs';
import { map, takeUntil, startWith, distinctUntilChanged } from 'rxjs/operators';

import {
    DECORATION_PIXELIZATION_COST,
    DECORATION_RESTRICT_PIXELIZATION_COST
} from '../constants';

const MAX_COUNT = 24;

@Component({
    selector: 'app-decoration-pixelization-form',
    templateUrl: './decoration-pixelization-form.component.pug',
    styleUrls: ['./decoration-pixelization-form.component.less']
})
export class DecorationPixelizationFormComponent implements OnInit, OnDestroy {
    _destroySbj: Subject<void> = new Subject();

    public form: FormGroup = new FormGroup({
        count: new FormControl(1, Validators.min(1)),
        theme: new FormControl('')
    });

    _pixelsSbj: BehaviorSubject<number> = new BehaviorSubject(0);
    public pixels$: Observable<number> = this._pixelsSbj.asObservable();
    @Input() set pixels(pixels: number) {
        this._pixelsSbj.next(pixels || 0);
    }

    _deluxeSbj: BehaviorSubject<boolean> = new BehaviorSubject(false);
    @Input() set pixelizationDeluxeUpgrade(value: boolean) {
        this._deluxeSbj.next(!!value);
    }
    get pixelizationDeluxeUpgrade(): boolean {
        return this._deluxeSbj.getValue();
    }

    _themesSbj: BehaviorSubject<IDecorationsTheme[]> = new BehaviorSubject([]);
    public themes$: Observable<any[]> = this._themesSbj.asObservable()
        .pipe(map((themes) => themes.filter(({ restricted }) => !restricted)))
        .pipe(map((themes) => themes.map(({ _id: value, name }) => ({ value, name }))))
        .pipe(map((themes) => [{ value: '', name: 'All' }, ...themes]));
    @Input() set themes(themes: IDecorationsTheme[]) {
        this._themesSbj.next(themes);
    }
    public selectedThemeColor$: Observable<string> = this.form.valueChanges
        .pipe(map(({ theme }) => this._themesSbj.getValue().find(({ _id }) => _id === theme )))
        .pipe(map((theme) => theme ? theme.color : null));

    _costSbj: BehaviorSubject<number> = new BehaviorSubject(DECORATION_PIXELIZATION_COST);
    cost$: Observable<number> = this._costSbj.asObservable();

    max$ = combineLatest(this.pixels$, this.cost$)
        .pipe(map(([pixels, cost]) => Math.floor(pixels / cost)))
        .pipe(map((max) => Math.min(max, MAX_COUNT)));

    @Output() pixelize: EventEmitter<any> = new EventEmitter();

    constructor(
    ) {
    }

    ngOnInit() {
        const theme$ = this.form.valueChanges.pipe(
            startWith(this.form.value),
            map(({ theme }) => theme),
            distinctUntilChanged()
        );

        combineLatest(theme$, this._deluxeSbj)
            .pipe(map(([theme, deluxe]) => theme && !deluxe
                ? DECORATION_RESTRICT_PIXELIZATION_COST : DECORATION_PIXELIZATION_COST))
            .pipe(distinctUntilChanged(), takeUntil(this._destroySbj))
            .subscribe((cost) => this._costSbj.next(cost));

        this.max$
            .pipe(distinctUntilChanged(), takeUntil(this._destroySbj))
            .subscribe((max) => {
                const control = this.form.get('count');
                control.setValidators([Validators.min(1), Validators.max(max)]);
                control.setValue(max > 0 ? Math.max(1, Math.min(control.value, max)) : 0);
            });
    }

    ngOnDestroy() {
        this._destroySbj.next();
        this._destroySbj.complete();
    }

    onPixelize() {
        if (this.form.valid) {
            this.pixelize.emit(this.form.value);
        }
    }
}
